// Cognito pool configuration — values injected at build time from .env
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

const USER_POOL_ID  = import.meta.env.VITE_COGNITO_USER_POOL_ID as string;
const CLIENT_ID     = import.meta.env.VITE_COGNITO_CLIENT_ID   as string;

if (!USER_POOL_ID || !CLIENT_ID) {
  console.error('[cognito] VITE_COGNITO_USER_POOL_ID or VITE_COGNITO_CLIENT_ID is missing!');
}

export const userPool = new CognitoUserPool({
  UserPoolId: USER_POOL_ID,
  ClientId:   CLIENT_ID,
});

// ── Auth helpers ───────────────────────────────────────────────────────────────

export interface SignInResult {
  type: 'success';
  session: CognitoUserSession;
  restaurantId: string;
  restaurantName: string;
  role: 'admin' | 'restaurant_staff';
}

export interface NewPasswordRequired {
  type: 'newPasswordRequired';
  cognitoUser: CognitoUser;
}

export type AuthResult = SignInResult | NewPasswordRequired;

/** Extract restaurantId from the ID token payload */
function getRestaurantIdFromSession(session: CognitoUserSession): string {
  const payload = session.getIdToken().decodePayload();
  return (payload['custom:restaurantId'] as string) || '';
}

function getRoleFromSession(session: CognitoUserSession): 'admin' | 'restaurant_staff' {
  const payload = session.getIdToken().decodePayload();
  const role = (payload['custom:role'] as string) || 'restaurant_staff';
  const groups = (payload['cognito:groups'] as string[]) || [];
  if (role === 'admin' || groups.includes('admin')) return 'admin';
  return 'restaurant_staff';
}

/** Sign in with username + password. Returns success or new-password-required challenge. */
export function signIn(username: string, password: string): Promise<AuthResult> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
    const authDetails = new AuthenticationDetails({ Username: username, Password: password });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess(session) {
        const restaurantId   = getRestaurantIdFromSession(session);
        const role           = getRoleFromSession(session);
        const payload        = session.getIdToken().decodePayload();
        const restaurantName = (payload['custom:restaurantName'] as string) || username;
        resolve({ type: 'success', session, restaurantId, restaurantName, role });
      },
      newPasswordRequired(_userAttributes, _requiredAttributes) {
        // First-time login — user must set a permanent password
        resolve({ type: 'newPasswordRequired', cognitoUser });
      },
      onFailure(err) {
        reject(err);
      },
    });
  });
}

/** Complete the forced password change on first login */
export function completeNewPassword(
  cognitoUser: CognitoUser,
  newPassword: string
): Promise<SignInResult> {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess(session) {
        const restaurantId   = getRestaurantIdFromSession(session);
        const role           = getRoleFromSession(session);
        const payload        = session.getIdToken().decodePayload();
        const restaurantName = (payload['custom:restaurantName'] as string) || cognitoUser.getUsername();
        resolve({ type: 'success', session, restaurantId, restaurantName, role });
      },
      onFailure(err) {
        reject(err);
      },
    });
  });
}

/** Sign out the current user */
export function signOut(): void {
  const user = userPool.getCurrentUser();
  if (user) user.signOut();
}

/** Restore session from localStorage (returns null if no valid session) */
export function getStoredSession(): Promise<SignInResult | null> {
  return new Promise((resolve) => {
    const user = userPool.getCurrentUser();
    if (!user) return resolve(null);

    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) return resolve(null);
      const restaurantId   = getRestaurantIdFromSession(session);
      const role           = getRoleFromSession(session);
      const payload        = session.getIdToken().decodePayload();
      const restaurantName = (payload['custom:restaurantName'] as string) || user.getUsername();
      resolve({ type: 'success', session, restaurantId, restaurantName, role });
    });
  });
}
